import { TopicFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import { Topic } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import { create } from 'zustand';

export type TopicFilterKey = TopicFilter;

/**
 * The session storage keys for the topic filters depending on where we want to hydrate the filters from later on
 */
export enum StorageSource {
  CREATE = 'TOPIC_CREATE',
  EDIT = 'TOPIC_EDIT',
  DUPLICATE = 'TOPIC_DUPLICATE'
}

/**
 * A set of operations that can be performed on the topic filter state
 */
type FilterOperations = {
  /**
   * Adds value(s) to a filter key
   */
  add: ({ key, values }: { key: TopicFilterKey; values: (string | number)[] }) => void;
  /**
   * Toggles a value in a filter key by adding it if it doesn't exist, or removing it if it does
   */
  toggle: ({ key, value }: { key: TopicFilterKey; value: string | number }) => void;
  /**
   * Replaces the values of a target filter key with the values of a source filter key.
   * Useful for swapping between include/exclude filters
   */
  replace: ({ source, target }: { source: TopicFilterKey; target: TopicFilterKey }) => void;
  /**
   * Removes (deletes) a value from a filter key
   */
  remove: ({ key, value }: { key: TopicFilterKey; value: string | number }) => void;
  /**
   * Clears all values for a given filter key
   */
  clear: ({ key }: { key: TopicFilterKey }) => void;
  /**
   * Clears all values for all filter keys
   */
  clearAll: () => void;
};

type FilterState = Record<TopicFilter, (string | number)[]>;

type TopicBuilderStore = {
  /**
   * The current state of the topic filters
   */
  filters: FilterState;

  /**
   * A flag to indicate if the topic filters are finished being hydrated
   */
  ready: boolean;

  /**
   * Actions that can be performed on the topic filter state
   */
  actions: {
    /**
     * Actions specific to session storage for saving and hydrating filters
     */
    storage: {
      /**
       * Saves the topic filters to session storage
       * @param topic the topic to save to session storage
       * @param source the source of the storage. This allows us to use different locations for hydrating the filters differently.
       * Example: From the setting page, we want to hydrate the filters from the edit page, so we use the EDIT source.
       * If we duplicate the topic, we want to clean up the storage under the EDIT source and hydrate the filters from the DUPLICATE source.
       */
      save: (topic: Topic, source: StorageSource) => void;
      /**
       * Hydrates the topic filters from session storage state. If a source is not provided, the filters will default to the empty state.
       */
      hydrate: (source?: StorageSource) => void;
      /**
       * Clears the topic filters from session storage and from the store
       */
      clear: (source: StorageSource) => void;
    };
    /**
     * Provides a function that can be used to manipulate the filter state using predefined operations
     * @example
     *  // Adding a filter
     *  setFilters(({ add }) => add({ key: 'includedKeywords', values: ['keyword1', 'keyword2'] }))
     *
     * // Toggling a filter
     * setFilters(({ toggle }) => toggle({ key: 'includedKeywords', value: 'keyword1' }))
     *
     * @param arg a function that takes a set of operations to manipulate the filter state
     */
    setFilters: (arg: ((operations: FilterOperations) => void) | Record<TopicFilter, string[] | number[]>) => void;
  };
};

const initialFilters: Record<TopicFilter, string[] | number[]> = {
  [TopicFilter.IncludedKeywordPhrases]: [],
  [TopicFilter.ExcludedKeywordPhrases]: [],
  [TopicFilter.IncludedKeywords]: [],
  [TopicFilter.ExcludedKeywords]: [],
  [TopicFilter.IncludedBrandIds]: [],
  [TopicFilter.ExcludedBrandIds]: [],
  [TopicFilter.IncludedCategoryIds]: [],
  [TopicFilter.ExcludedCategoryIds]: [],
  [TopicFilter.IncludedSubCategoryIds]: [],
  [TopicFilter.ExcludedSubCategoryIds]: []
};

const useTopicBuilderStore = create<TopicBuilderStore>((set) => ({
  filters: {
    ...initialFilters
  },

  ready: false,

  actions: {
    storage: {
      save: (topic, source) => {
        const filters = {
          [TopicFilter.IncludedKeywordPhrases]: topic?.includedKeywordPhrases || [],
          [TopicFilter.ExcludedKeywordPhrases]: topic?.excludedKeywordPhrases || [],
          [TopicFilter.IncludedKeywords]: topic?.includedKeywords || [],
          [TopicFilter.ExcludedKeywords]: topic?.excludedKeywords || [],
          [TopicFilter.IncludedBrandIds]: topic?.includedBrandIds || [],
          [TopicFilter.ExcludedBrandIds]: topic?.excludedBrandIds || [],
          [TopicFilter.IncludedCategoryIds]: topic?.includedCategoryIds || [],
          [TopicFilter.ExcludedCategoryIds]: topic?.excludedCategoryIds || [],
          [TopicFilter.IncludedSubCategoryIds]: topic?.includedSubCategoryIds || [],
          [TopicFilter.ExcludedSubCategoryIds]: topic?.excludedSubCategoryIds || []
        };
        sessionStorage.setItem(source, JSON.stringify(filters));
      },

      hydrate: (source?: StorageSource) => {
        let newFilters: FilterState;

        switch (source) {
          case StorageSource.CREATE:
            newFilters = initialFilters;
            break;
          case StorageSource.DUPLICATE:
          case StorageSource.EDIT:
            newFilters = JSON.parse(sessionStorage.getItem(source) || JSON.stringify(initialFilters));
            break;
          default:
            newFilters = initialFilters;
        }

        set({ filters: newFilters, ready: true });
      },

      clear: (source: StorageSource) => {
        sessionStorage.removeItem(source);
        set({ filters: initialFilters, ready: false });
      }
    },

    setFilters: (arg) => {
      if (typeof arg === 'function') {
        set((state) => {
          const operations: FilterOperations = {
            add: ({ key, values }) => {
              set({
                filters: {
                  ...state.filters,
                  [key]: [...state.filters[key], ...values]
                }
              });
            },

            toggle: ({ key, value }) => {
              set({
                filters: {
                  ...state.filters,
                  [key]: state.filters[key].includes(value)
                    ? state.filters[key].filter((item) => item !== value)
                    : [...state.filters[key], value]
                }
              });
            },

            replace: ({ source, target }) => {
              const sourceValues = state.filters[source];
              set({
                filters: {
                  ...state.filters,
                  [source]: [],
                  [target]: sourceValues
                }
              });
            },

            remove: ({ key, value }) => {
              set({
                filters: {
                  ...state.filters,
                  [key]: state.filters[key].filter((item) => item !== value)
                }
              });
            },

            clear: ({ key }) => {
              set({ filters: { ...state.filters, [key]: [] } });
            },

            clearAll: () => {
              set({ filters: initialFilters });
            }
          };

          arg(operations);
          return {};
        });
      } else {
        set({ filters: arg });
      }
    }
  }
}));

/**
 * Hook to access the topic builder actions without subscribing to entire store
 */
export const useTopicBuilderActions = () => useTopicBuilderStore((state) => state.actions);

/**
 * Hook to access the topic builder filters without subscribing to entire store
 */
export const useTopicBuilderFilters = () =>
  useTopicBuilderStore((state) => ({ filters: state.filters, ready: state.ready }));
